import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { AuthUser } from '@just-frotas/shared';
import { useAuth } from './AuthContext';

// Perfil de acesso e VIEW de menu lateral. Deriva dos vinculos do user
// (cliente / rede / admin) e e ortogonal a permissions, scope, activeClient
// e activeNetwork. Permission ainda filtra item dentro do perfil.
//
// Regras (decididas em 2026-05-04 - PLAN-PERFIS-MENU):
//   - 3 perfis: frotista | posto | admin.
//   - Default ao logar: prioridade ADMIN > FROTISTA > POSTO,
//     respeitando localStorage se valido pra availableProfiles.
//   - Trocar perfil: queryClient.clear() + navigate('/') + persistir.
//   - activeClientUuid e activeNetworkId NAO sao tocados (decisao 3.7).

export type ProfileKey = 'frotista' | 'posto' | 'admin';

export interface ProfileContextValue {
  availableProfiles: ProfileKey[];
  activeProfile: ProfileKey | null;
  setActiveProfile: (next: ProfileKey) => void;
  // True quando user tem mais de 1 perfil disponivel.
  // ProfileSwitcher usa isso pra decidir entre chip estatico e dropdown.
  canSwitch: boolean;
}

const STORAGE_KEY = 'just_active_profile';
const VALID_KEYS: ProfileKey[] = ['frotista', 'posto', 'admin'];

const ProfileContext = createContext<ProfileContextValue | undefined>(undefined);

function isProfileKey(value: unknown): value is ProfileKey {
  return typeof value === 'string' && (VALID_KEYS as string[]).includes(value);
}

function readPersisted(): ProfileKey | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return isProfileKey(raw) ? raw : null;
  } catch {
    // localStorage pode estar bloqueado (modo privado etc) - degrada gracioso
    return null;
  }
}

function writePersisted(value: ProfileKey): void {
  try {
    localStorage.setItem(STORAGE_KEY, value);
  } catch {
    /* noop */
  }
}

export function deriveAvailableProfiles(user: AuthUser | null): ProfileKey[] {
  if (!user) return [];
  // Admin tem acesso aos 3 perfis (decisao CEO 2026-05-04): permite que
  // admins da plataforma vejam o ponto de vista de frotista e posto sem
  // precisar de vinculo direto. Ordem aqui nao importa - pickDefaultProfile
  // aplica a prioridade ADMIN > FROTISTA > POSTO no default.
  if (user.isAdmin) return ['frotista', 'posto', 'admin'];
  const out: ProfileKey[] = [];
  if (user.clientUuid !== null || user.clientGroupId !== null) out.push('frotista');
  if (user.networkId !== null || user.networkGroupId !== null) out.push('posto');
  return out;
}

export function pickDefaultProfile(
  available: ProfileKey[],
  persisted: ProfileKey | null,
): ProfileKey | null {
  if (available.length === 0) return null;
  if (persisted && available.includes(persisted)) return persisted;
  // Prioridade: Admin > Frotista > Posto.
  if (available.includes('admin')) return 'admin';
  if (available.includes('frotista')) return 'frotista';
  return 'posto';
}

export function ProfileProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const availableProfiles = useMemo(() => deriveAvailableProfiles(user), [user]);

  const [activeProfile, setActiveProfileState] = useState<ProfileKey | null>(() =>
    pickDefaultProfile(availableProfiles, readPersisted()),
  );

  // Reconciliacao: quando user muda (refresh do /auth/me, troca de cliente,
  // logout/login com user diferente) os perfis disponiveis podem mudar.
  // Se o ativo nao esta mais disponivel ou ainda nao foi setado, recalcula.
  useEffect(() => {
    if (!activeProfile || !availableProfiles.includes(activeProfile)) {
      setActiveProfileState(pickDefaultProfile(availableProfiles, readPersisted()));
    }
  }, [availableProfiles, activeProfile]);

  const setActiveProfile = useCallback(
    (next: ProfileKey) => {
      if (!availableProfiles.includes(next)) return;
      setActiveProfileState(next);
      writePersisted(next);
      // Cache pode conter dados nao relevantes (queries do perfil anterior).
      // activeClientUuid / activeNetworkId vivem no JWT, nao no React Query.
      queryClient.clear();
      navigate('/', { replace: true });
    },
    [availableProfiles, queryClient, navigate],
  );

  const value = useMemo<ProfileContextValue>(
    () => ({
      availableProfiles,
      activeProfile,
      setActiveProfile,
      canSwitch: availableProfiles.length > 1,
    }),
    [availableProfiles, activeProfile, setActiveProfile],
  );

  return <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>;
}

export function useProfile(): ProfileContextValue {
  const ctx = useContext(ProfileContext);
  if (!ctx) throw new Error('useProfile deve ser usado dentro de <ProfileProvider>');
  return ctx;
}
